import { resolveMarketingSiteOrigin } from './absoluteSiteUrl.js';
import { HUB_PAGE_SEO_LABELS, resolveTenantMarketingPageUrl } from './marketingHubSeo.js';
import { normalizeMarketingPath } from './marketingLandingSeo.js';
import { mergeImportedLandingPageSeo } from './marketingSeoPreview.js';
import type { HubPageSeoEntry, PublicMarketingConfig } from './publicMarketingConfig.js';

/** Portable JSON shape (matches `public_marketing_config` SEO keys). */
export type MarketingSeoExportBundle = {
  homepage_seo_title?: string;
  homepage_seo_description?: string;
  default_og_image_url?: string;
  hub_page_seo?: Partial<Record<string, HubPageSeoEntry>>;
  landing_page_seo?: Partial<Record<string, HubPageSeoEntry>>;
};

export type MarketingSeoOverrideAuditRow = {
  path: string;
  kind: 'homepage' | 'hub' | 'landing';
  label: string;
  canonicalUrl: string;
  hasTitle: boolean;
  hasDescription: boolean;
};

function filterNonEmptyPathSeo(
  map: Partial<Record<string, HubPageSeoEntry>>,
): Partial<Record<string, HubPageSeoEntry>> {
  const filtered: Partial<Record<string, HubPageSeoEntry>> = {};
  for (const [path, entry] of Object.entries(map)) {
    if (!entry) continue;
    const title = entry.title?.trim() ?? '';
    const description = entry.description?.trim() ?? '';
    if (!title && !description) continue;
    filtered[path] = { title, description };
  }
  return filtered;
}

/** Export only SEO override fields (non-empty) for backup or tenant migration. */
export function exportMarketingSeoOverrides(config: PublicMarketingConfig): MarketingSeoExportBundle {
  const bundle: MarketingSeoExportBundle = {};
  const homepageTitle = config.homepageSeoTitle.trim();
  const homepageDescription = config.homepageSeoDescription.trim();
  if (homepageTitle) bundle.homepage_seo_title = homepageTitle;
  if (homepageDescription) bundle.homepage_seo_description = homepageDescription;
  const defaultOgImageUrl = config.defaultOgImageUrl.trim();
  if (defaultOgImageUrl) bundle.default_og_image_url = defaultOgImageUrl;

  const hubPageSeo = filterNonEmptyPathSeo(config.hubPageSeo);
  const landingPageSeo = filterNonEmptyPathSeo(config.landingPageSeo);
  if (Object.keys(hubPageSeo).length > 0) bundle.hub_page_seo = hubPageSeo;
  if (Object.keys(landingPageSeo).length > 0) bundle.landing_page_seo = landingPageSeo;
  return bundle;
}

export function serializeMarketingSeoExport(config: PublicMarketingConfig): string {
  return JSON.stringify(exportMarketingSeoOverrides(config), null, 2);
}

function mergePathSeoImport(
  current: Partial<Record<string, HubPageSeoEntry>>,
  imported: unknown,
  options?: { restrictToHubPaths?: boolean },
): Partial<Record<string, HubPageSeoEntry>> {
  if (!imported || typeof imported !== 'object') {
    throw new Error('Import must be a JSON object keyed by path');
  }

  const merged = { ...current };
  for (const [rawPath, rawEntry] of Object.entries(imported as Record<string, unknown>)) {
    const path = normalizeMarketingPath(rawPath);
    if (!path || path === '/') continue;
    if (options?.restrictToHubPaths && !HUB_PAGE_SEO_LABELS[path]) continue;
    if (!rawEntry || typeof rawEntry !== 'object') continue;
    const entry = rawEntry as Record<string, unknown>;
    const title = String(entry.title ?? '').trim();
    const description = String(entry.description ?? '').trim();
    if (!title && !description) continue;
    merged[path] = {
      title: title || merged[path]?.title || '',
      description: description || merged[path]?.description || '',
    };
  }
  return merged;
}

/** Merge imported hub SEO map into existing config (org/platform safe). */
export function mergeImportedHubPageSeo(
  current: PublicMarketingConfig['hubPageSeo'],
  imported: unknown,
): PublicMarketingConfig['hubPageSeo'] {
  return mergePathSeoImport(current, imported, { restrictToHubPaths: true });
}

function isMarketingSeoExportBundle(value: unknown): value is MarketingSeoExportBundle {
  if (!value || typeof value !== 'object') return false;
  const o = value as Record<string, unknown>;
  return (
    'homepage_seo_title' in o ||
    'homepage_seo_description' in o ||
    'default_og_image_url' in o ||
    'hub_page_seo' in o ||
    'landing_page_seo' in o
  );
}

function splitFlatPathSeoImport(imported: Record<string, unknown>): {
  hub: Record<string, unknown>;
  landing: Record<string, unknown>;
} {
  const hub: Record<string, unknown> = {};
  const landing: Record<string, unknown> = {};
  for (const [rawPath, entry] of Object.entries(imported)) {
    const path = normalizeMarketingPath(rawPath);
    if (!path || path === '/') continue;
    if (HUB_PAGE_SEO_LABELS[path]) {
      hub[path] = entry;
    } else {
      landing[path] = entry;
    }
  }
  return { hub, landing };
}

/** Merge a full SEO export bundle (or flat path map) into public marketing config. */
export function mergeImportedMarketingSeoBundle(
  current: PublicMarketingConfig,
  imported: unknown,
): PublicMarketingConfig {
  if (!imported || typeof imported !== 'object') {
    throw new Error('Import must be a JSON object');
  }

  if (isMarketingSeoExportBundle(imported)) {
    const bundle = imported as MarketingSeoExportBundle;
    const next = { ...current };
    if (typeof bundle.homepage_seo_title === 'string' && bundle.homepage_seo_title.trim()) {
      next.homepageSeoTitle = bundle.homepage_seo_title.trim();
    }
    if (typeof bundle.homepage_seo_description === 'string' && bundle.homepage_seo_description.trim()) {
      next.homepageSeoDescription = bundle.homepage_seo_description.trim();
    }
    if (typeof bundle.default_og_image_url === 'string' && bundle.default_og_image_url.trim()) {
      next.defaultOgImageUrl = bundle.default_og_image_url.trim();
    }
    if (bundle.hub_page_seo) {
      next.hubPageSeo = mergeImportedHubPageSeo(next.hubPageSeo, bundle.hub_page_seo);
    }
    if (bundle.landing_page_seo) {
      next.landingPageSeo = mergeImportedLandingPageSeo(next.landingPageSeo, bundle.landing_page_seo);
    }
    return next;
  }

  const { hub, landing } = splitFlatPathSeoImport(imported as Record<string, unknown>);
  return {
    ...current,
    ...(Object.keys(hub).length > 0 ? { hubPageSeo: mergeImportedHubPageSeo(current.hubPageSeo, hub) } : {}),
    ...(Object.keys(landing).length > 0
      ? { landingPageSeo: mergeImportedLandingPageSeo(current.landingPageSeo, landing) }
      : {}),
  };
}

export function resolveMarketingSeoPreviewDisplayUrl(path: string, websiteUrl?: string): string {
  const origin = resolveMarketingSiteOrigin(websiteUrl);
  const fallback = `${origin}${path === '/' ? '' : path}`;
  const canonical = resolveTenantMarketingPageUrl(websiteUrl, path, fallback);
  try {
    const url = new URL(canonical);
    const displayPath = path === '/' ? '' : path;
    return `${url.hostname}${displayPath}`;
  } catch {
    return `www.example.com${path === '/' ? '' : path}`;
  }
}

export function resolveMarketingSeoPreviewCanonicalUrl(path: string, websiteUrl?: string): string {
  const origin = resolveMarketingSiteOrigin(websiteUrl);
  const fallback = `${origin}${path === '/' ? '' : path}`;
  return resolveTenantMarketingPageUrl(websiteUrl, path, fallback);
}

/** List configured SEO overrides with tenant canonical URLs for audit. */
export function auditMarketingSeoOverrides(
  config: PublicMarketingConfig,
  websiteUrl?: string,
): MarketingSeoOverrideAuditRow[] {
  const rows: MarketingSeoOverrideAuditRow[] = [];

  if (config.homepageSeoTitle.trim() || config.homepageSeoDescription.trim()) {
    rows.push({
      path: '/',
      kind: 'homepage',
      label: 'Homepage',
      canonicalUrl: resolveMarketingSeoPreviewCanonicalUrl('/', websiteUrl),
      hasTitle: Boolean(config.homepageSeoTitle.trim()),
      hasDescription: Boolean(config.homepageSeoDescription.trim()),
    });
  }

  for (const [path, entry] of Object.entries(config.hubPageSeo)) {
    if (!entry?.title?.trim() && !entry?.description?.trim()) continue;
    rows.push({
      path,
      kind: 'hub',
      label: HUB_PAGE_SEO_LABELS[path] ?? path,
      canonicalUrl: resolveMarketingSeoPreviewCanonicalUrl(path, websiteUrl),
      hasTitle: Boolean(entry.title?.trim()),
      hasDescription: Boolean(entry.description?.trim()),
    });
  }

  for (const [path, entry] of Object.entries(config.landingPageSeo)) {
    if (!entry?.title?.trim() && !entry?.description?.trim()) continue;
    rows.push({
      path,
      kind: 'landing',
      label: path,
      canonicalUrl: resolveMarketingSeoPreviewCanonicalUrl(path, websiteUrl),
      hasTitle: Boolean(entry.title?.trim()),
      hasDescription: Boolean(entry.description?.trim()),
    });
  }

  return rows.sort((a, b) => a.path.localeCompare(b.path));
}
